<template>
    <div class="checkout-item" tabindex="0" :aria-label="`${item.name} for checkout`">
        <template v-if="!isMinified">
			<p class="is-size-6">
				<strong>{{ item.name }}</strong>
				<span class="is-size-6 is-pulled-right amount">
					{{ (!isRecentOrder) ? itemPrice(index) : getRecentOrderPrice() }}
				</span>
				<p v-for="discount in getFlattenedDiscounts" :key="discount.id" tabindex="0" :aria-label="`discount applied to ${item.name}: ${discount.discountAmount}`">
					Discount ({{ discount.name }}):
					<span class="is-size-6 is-pulled-right amount discounted--amount">
						-{{ discount.discountAmount }}
					</span>
					<template v-if="isDiscountOffer(discount)">
						<offer-summary-item :offer="discount" />
					</template>
				</p>
			<cart-item-info-siblings 
				:options="item.options" 
				:isRecentOrder="isRecentOrder" />
            <div v-for="bundleItem in bundleItems()" :key="bundleItemKey(bundleItem)">
                <h4>{{ bundleItem.name }}</h4>
                <cart-item-info-siblings :options="bundleItem.options" />
            </div>
            <div class="item-quantity-actions" :class="alignEditForBundle">
                <BaseQuantityButtons
                    :quantity="item.quantity"
					:settings="{ item: item }"
                    @incrementQuantity="increment()"
                    @decrementQuantity="decrement()"
					@setInputQuantity="setQuantityFromInput" v-if="!isBundle"/>
					<a tabindex='0'
						class="edit--action"
						@click="editItem()"
						@keydown.enter="editItem()">Edit</a>
					<a tabindex='0'
						@click="removeItem()"
						@keydown.enter="removeItem()">Delete</a>
            </div>
        </template>
        <template v-else>
			<div>
				<div class="is-size-6">
					<span class="has-text-grey">{{ item.quantity }}</span>
					<span class="is-bold">{{ item.name }}</span>
					<span class="is-size-6 is-pulled-right has-text-grey">
						{{ (!isRecentOrder) ? itemPrice(index) : getRecentOrderPrice() }}
					</span>
					<p v-for="discount in item.discounts" :key="discount.id">
						<template v-if="showDiscount(discount)">
							<span class="is-size-6-5">Discount ({{ discount.name }}):</span>
							<span class="is-size-6 is-pulled-right amount discounted--amount">
								-{{ displayDiscount(discount) }}
							</span>
							<template v-if="isDiscountOffer(discount)">
								<offer-summary-item :offer="discount" />
							</template>
						</template>
					</p>
				</div>
				<cart-item-info-siblings 
					:options="item.options"
					:isMinified="isMinified"
					:isRecentOrder="isRecentOrder" />
                <div class="indent" v-for="bundleItem in bundleItems()" :key="bundleItemKey(bundleItem)">
                    <h4>{{ bundleItem.name }}</h4>
                    <cart-item-info-siblings :options="bundleItem.options" :isMinified="isMinified" />
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import EventBus from '@EventBus';
import {printCompactedOptions} from "@/utils/helper.functions";
import Formatter from "@/utils/Formatter";
import checkoutPricesMixin from '@/components/mixins/checkoutPricesMixin';
import couponMixin from '@/components/mixins/couponMixin';
import CartItemInfoSiblings from '@/components/menu/CartItemInfoSiblings.vue';
import OfferSummaryItem from '@/components/checkout/OfferSummaryItem.vue';
import { mapGetters, mapActions } from 'vuex';
import {CART_OPTION, CART} from "@/stores/defs";
import { forEach, keys, find, } from 'lodash';

export default {
	mixins: [
		checkoutPricesMixin,
		couponMixin,
	],
	data() {
		return {
			discount: null
		}
	},
    components: {
		'cart-item-info-siblings': CartItemInfoSiblings,
		'offer-summary-item': OfferSummaryItem,
    },
    computed: {
        ...mapGetters({
            listOptions: CART_OPTION.LIST_ALL,
            itemCount: CART.ITEM_COUNT
		}),
		isBundle() {
			return (!!this.bundleItems());
		},
		// TODO style for bundles and normal items should be refactored
		// or should look identical assuming bundle quantities gets fixed
		alignEditForBundle() {
			if (this.isBundle) {
				return 'no-quantity';
			}
			return '';
		},
		getFlattenedDiscounts() {
			const displayDiscounts = [];
			const discounts = this.item.discounts;
			if (!discounts) {
				return;
			}
			let discountsMap = {};
			forEach(discounts, (discount) => {
				discountsMap[discount.id] = 0;
			});
			forEach(discounts, (discount) => {
				discountsMap[discount.id]++;
			});
			const discountKeys = keys(discountsMap);
			forEach(discountKeys, (key) => {
				const discount = find(discounts, (_discount) => {
					return _discount.id === key;
				});
				discount['quantity'] = discountsMap[key];
				discount['discountAmount'] = this.displayDiscountPrice(discount);
				displayDiscounts.push(discount);
			});
			return displayDiscounts;
		},
    },
    methods: {
		...mapActions({
			setItemQuantity: CART.SET_ITEM_QUANTITY,
		}),
		setQuantityFromInput(value) {
			this.item.invalidatePriceCache();
			this.setItemQuantity({
				_item: this.item,
				quantity: value,
			});
		},
        printCompactedOptions: printCompactedOptions,
        centsToDollars: Formatter.centsToDollars,
        increment(event) {
			this.$emit('incrementQuantity', event);
			EventBus.$emit('recalculate-order');
        },
        decrement(event) {
			this.$emit('decrementQuantity', event);
			EventBus.$emit('recalculate-order');
        },
        editItem(event) {
            this.$emit('editItem', event);
        },
        removeItem(event) {
            this.$emit('removeItem', event);
        },
        bundleItems() {
            const bundle = this.item.bundle;
            if (bundle) {
                return bundle.items;
            }
            return null;
        },
        bundleItemKey(bundleItem) {
            const groupId = bundleItem.parentGroupId;
            const random = Math.random() * 10000;
            return `${groupId}-${random}-${bundleItem.id}`;
		},
		showDiscount(discount) {
			if (this.discount !== null) {
				return true;
			}
			const item = this.item;
			const discountValue = discount.value;
			if (discountValue === 0) {
				return false;
			}
			return (discount.id);
		},
		itemPrice(index) {
			let itemPrice = 0;
			const item = this.item;
			if (item.bundle) {
				itemPrice = item.bundle.getBundleTotal();
			} else {
				itemPrice = this.getItemTotal(index);
			}
			return this.centsToDollars(itemPrice);
		},
		displayDiscount(discount) {
			if (discount.discountAmount) {
				return discount.discountAmount;
			}
			return this.centsToDollars(discount.amount);
		},
		displayPrice(amount) {
			return this.centsToDollars(amount);
		},
		displayDiscountPrice(discount) {
			const item = this.item;
			let discountPrice = this.discount;
			if (!this.discount) {
				const discountQuantity = discount['quantity'] || 1;
				const roundedDiscount = Math.floor(this.getDiscountPriceForItem(discount, item));
				discountPrice = roundedDiscount * discountQuantity;
				this.discount = discountPrice;
			}
			let displayPrice = null; 
			if (this.isBundle) {
				const bundleItems = item.bundle.items;
				const bundleOptionExceptions = this.getItemOptionExceptions(bundleItems);
				const bundlePrice = item.bundle.getBundleBasePrice(bundleOptionExceptions);
				let bundleDiscount = 0;
				if (!bundlePrice || discount.type == '2') {
					bundleDiscount = discountPrice;
				} else {
					bundleDiscount = bundlePrice - discountPrice;
				}
				displayPrice = this.displayPrice(bundleDiscount);
			} else {
				const itemPrice = item.cachedPrice || item.getSingleItemTotal();
				const itemTotal = itemPrice * item.quantity;
				discountPrice = Math.min(itemTotal, discountPrice);
				displayPrice = this.displayPrice(discountPrice);
			}
			return displayPrice;
		},
		isDiscountOffer(discount) {
			if (discount.__mc === "OfferModel") {
				return true;
			}
			return false;
		},
		getRecentOrderPrice() {
			const atOrderConfirmation = (this.$route.name === 'order-confirmation');
			const item = this.item;
			const displayPrice = this.centsToDollars(item.price);
			const displayTotal = this.centsToDollars(item.getItemTotal());
			return atOrderConfirmation ? displayPrice : displayTotal;
		},
	},
    props: {
        index: Number,
        item: {
            title: String,
            options: Object,
            quantity: Number,
            discounts: Array,
        },
        isMinified: {
            type: Boolean,
            default: false
        },
        isRecentOrder: {
            type: Boolean,
            default: false
        },
    },
    watch: {
        item: {
            handler(newItem, oldItem) {
				const newString = JSON.stringify(newItem);
				const oldString = JSON.stringify(oldItem);
				if (newString === oldString) {
					return;
				}
                if (newItem) {
					this.$emit("change");
                }
            },
            deep: true
        }
    }
}
</script>

<style lang="scss">
    @import 'globalStyles';
    .checkout-item {
        width: 100%;
    }
    .small-option-txt {
        font-size: .9rem;
    }
	.item-quantity-actions {
		display: flex;
		justify-content: space-between;
		max-width: 240px;
		&.no-quantity {
			max-width: 90px;
		}
	}
	.item-quantity-actions a {
		color: $navigationItemColor;
		margin-top: 1.2em;
	}
	.no-margin-left {
		margin-left: 0;
	}
    .indent {
        padding-left: 15px;
	}
	.amount {
		font-weight: bold;
	}
	.discounted--amount {
		color: $discountColor;
	}
	@include mobile {
		.item-quantity-actions {
			a {
				margin-top: 0;
			}
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/checkout/CheckoutItem.vue